/**
 * PosProductGrid — product catalogue browser for the till.
 *
 * Loads all active POS products on mount, groups them by category,
 * and renders as a clickable grid. Clicking a product calls onAddProduct.
 */

import { listPosProducts } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { PosProduct } from '@/lib/types';
import { PackageOpen } from 'lucide-react';
import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** Called when the operator clicks a product tile. */
  onAddProduct: (product: PosProduct, quantity?: number) => void;
}

// ---------------------------------------------------------------------------
// Grouping helper
// ---------------------------------------------------------------------------

interface ProductGroup {
  category: string;
  products: PosProduct[];
}

function groupProducts(products: PosProduct[]): ProductGroup[] {
  const map = new Map<string, PosProduct[]>();
  for (const p of products) {
    const key = p.category?.trim() || 'Other';
    const existing = map.get(key);
    if (existing) {
      existing.push(p);
    } else {
      map.set(key, [p]);
    }
  }
  // Sort within each group by position
  for (const group of map.values()) {
    group.sort((a, b) => a.position - b.position);
  }
  // Return groups sorted alphabetically, but "Other" last
  const entries = [...map.entries()].sort(([a], [b]) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
  return entries.map(([category, products]) => ({ category, products }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PosProductGrid({ onAddProduct }: Props) {
  const [products, setProducts] = useState<PosProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPosProducts(true)
      .then(({ products: p }) => {
        if (!cancelled) {
          setProducts(p);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load products.');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton tiles
            <div key={i} className="h-20 animate-pulse rounded-xl bg-secondary" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <PackageOpen className="size-10 text-muted-foreground" strokeWidth={1.25} />
        <p className="text-sm text-muted-foreground">
          No products in catalogue.{' '}
          <a
            href="/settings/pos/products"
            className="text-primary underline-offset-2 hover:underline"
          >
            Add some at /settings/pos/products.
          </a>
        </p>
      </div>
    );
  }

  const groups = groupProducts(products);

  return (
    <div className="space-y-6 p-5">
      {groups.map((group) => (
        <section key={group.category}>
          <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {group.category}
          </h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
            {group.products.map((product) => {
              const isTracked = product.stockTrackingEnabled === 1;
              const isOutOfStock = isTracked && product.stockQuantity <= 0;
              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => onAddProduct(product, 1)}
                  className="relative flex flex-col items-start rounded-xl border border-border bg-surface p-3 text-left transition-colors hover:bg-secondary/60 press active:scale-[0.98]"
                  data-testid={`product-tile-${product.id}`}
                >
                  <span className="line-clamp-2 text-sm font-medium leading-snug">
                    {product.name}
                  </span>
                  <span className="mt-1.5 num text-xs font-medium text-primary">
                    {formatPriceMinor(product.priceMinor, product.currency)}
                  </span>
                  {isTracked && (
                    <span
                      className={`mt-1 num text-[11px] font-medium ${isOutOfStock ? 'text-destructive' : 'text-muted-foreground'}`}
                    >
                      {isOutOfStock ? 'Out of stock' : `${product.stockQuantity} in stock`}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
